import React, { ReactElement } from 'react';
import { Provider } from 'react-redux';
import { matchPath } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ConnectedRouter } from 'connected-react-router';
import { enableES5 } from 'immer';
import { App } from 'b2b-apps/shop/app/components/App/App';
import { DisabledCookiesPage } from 'b2b-apps/shop/app/components/DisabledCookiesPage';
import { ErrorBoundary } from 'b2b-apps/shop/app/components/ErrorBoundary';
import { rootSaga } from 'b2b-apps/shop/common/redux/saga';
import { configureStore } from 'b2b-apps/shop/common/redux/store';
import { State } from 'b2b-apps/shop/common/redux/types';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { generateUnique } from 'b2b-apps/shop/common/utils/key/key';
import { logger } from 'b2b-apps/shop/common/utils/logger/logger';
import { getAreCookiesEnabled } from 'b2b-apps/shop/common/utils/userCookie/userCookie';
import { config } from 'b2b-apps/shop/config';
import { getActiveLanguage } from 'b2b-apps/shop/i18n/redux/selectors';
import { ROUTES, RouteType } from 'b2b-apps/shop/routes/components/Router';
import { version } from '../../../../../../package.json';
import './common.scss';
import './Root.scss';

enableES5();

const Root = (): ReactElement => {
  let initialState: State = {};

  try {
    initialState = JSON.parse(decodeURIComponent(window.__INITIAL_STATE__));
  } catch (error) {
    logger.error('Could not load initial state');
  }

  const {
    history,
    store,
  } = configureStore('/', initialState);

  store.runSaga(rootSaga);

  const activeLanguage = getActiveLanguage(initialState);

  // Init Sentry
  if (process.env.NODE_ENV !== 'development') {
    const sentryKey = config.sentry.publicKey;
    const sentryAppId = config.sentry.appId;
    const sentrySampleRate = config.sentry.sampleRate || 1;

    Sentry.init({
      dsn: `https://${sentryKey}@sentry.io/${sentryAppId}`,
      release: `v${version}`,
      environment: config.env,
      initialScope: {
        tags: {
          shop: config.shopName,
        },
      },
      tracesSampleRate: sentrySampleRate,
      integrations: [
        new Integrations.BrowserTracing({
          // Can also use reactRouterV3Instrumentation or reactRouterV4Instrumentation
          routingInstrumentation: Sentry.reactRouterV5Instrumentation(
            history,
            Object.values(ROUTES).reduce(
              (result: { path?: string, exact: boolean }[], route: RouteType) => {
                if (route.paths[activeLanguage]) {
                  result.push({
                    path: route.paths[activeLanguage],
                    exact: route.exact || false,
                  });
                }

                return result;
              },
              [],
            ),
            matchPath,
          ),
        }),
      ],
    });
  }

  if (!isServer && !window.requestId) {
    window.requestId = generateUnique();
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ConnectedRouter history={history}>
          {getAreCookiesEnabled() ? <App /> : <DisabledCookiesPage />}
        </ConnectedRouter>
      </Provider>
    </ErrorBoundary>
  );
};

export { Root };
